import React, { useCallback, useEffect, useMemo, useState } from "react";
import NavTabs from "~components/NavTabs";
import ReportFilterBar from "../../components/ReportFilterBar";
import VolumeSection from "../../components/VolumeSection";
import GraficoClienteStatic from "../../components/GraficoClienteStatic";
import SelfServicesChart from "../../components/SelfServicesChart";
import ClientShareChart from "../../components/ClientShareChart";
import { useClienteShareWinLossData } from "../../hooks/useClienteShareWinLossData";
import { useFatsData } from "../../hooks/useFatsData";
import { buildMesOptionsFromDados } from "../../utils/formatters";

const tabs = [
  { name: "Carteira", link: "/gestao-de-vendas/relatorio-de-vendas/carteira", className: "w-[200px]" },
  { name: "Clientes", link: "/gestao-de-vendas/relatorio-de-vendas/clientes", className: "w-[200px]" },
];

const visaoOptions = [
  { value: "mensal", label: "Visão Mensal" },
  { value: "semanal", label: "Visão Semanal" },
];

const todasMicrosOption = { value: "", label: "TODAS" };

const defaultMicro = "";

const CarteiraPage = () => {
  const [searchValue, setSearchValue] = useState("");
  const [visao, setVisao] = useState("mensal");
  const [mesSelecionado, setMesSelecionado] = useState(null);
  const [micro, setMicro] = useState(defaultMicro);
  const [filters, setFilters] = useState({ grupoMaterialId: 0, produtoId: 0 });

  const { volumeCards, govs, faturamentosData, filtros, positivacoesChartData, mesesDeCards, updatedAt, isRefreshing: refreshingFats } =
    useFatsData(mesSelecionado, {
      grupoMaterialId: filters.grupoMaterialId,
      produtoId: filters.produtoId,
    });

  const mesOptions = useMemo(() => buildMesOptionsFromDados(mesesDeCards), [mesesDeCards]);
  const defaultMes = mesOptions[0]?.value ?? "";
  const mes = mesSelecionado ?? defaultMes;

  const grupoProdutoNome = useMemo(() => {
    if (!filters.grupoMaterialId) return "todos";
    const match = (filtros?.gruposMateriais ?? []).find((g) => g.id === filters.grupoMaterialId);
    return match?.nome ?? "todos";
  }, [filters.grupoMaterialId, filtros]);

  const produtoNome = useMemo(() => {
    if (!filters.produtoId) return "todos";
    const match = (filtros?.produtos ?? []).find((p) => p.id === filters.produtoId);
    return match?.nome ?? "todos";
  }, [filters.produtoId, filtros]);

  const hasActiveFilters = mes !== defaultMes || micro !== defaultMicro;

  const clearPageFilters = useCallback(() => {
    setMesSelecionado(null);
    setMicro(defaultMicro);
  }, []);

  const { clientShareChartData, micros, isRefreshing: refreshingShare } = useClienteShareWinLossData({
    grupoProduto: grupoProdutoNome,
    produto: produtoNome,
    micro,
    mes,
  });

  const microOptions = useMemo(() => {
    const porId = new Map();

    for (const m of micros ?? []) {
      if (m?.id == null || !m?.nome) continue;
      porId.set(String(m.id), { value: String(m.id), label: m.nome });
    }

    const lista = [...porId.values()].sort((a, b) => a.label.localeCompare(b.label));

    return [todasMicrosOption, ...lista];
  }, [micros]);

  useEffect(() => {
    if (!micro) return;
    if (microOptions.length <= 1) return;
    if (microOptions.some((o) => o.value === micro)) return;
    setMicro(defaultMicro);
  }, [microOptions, micro]);

  const isRefreshing = refreshingShare || refreshingFats;

  const cleanFilters = useCallback(() => {
    setFilters({ grupoMaterialId: 0, produtoId: 0 });
  }, []);

  const hasGrupoOuProdutoFiltro = !!filters.grupoMaterialId || !!filters.produtoId;

  const selects = [
    {
      id: "visao",
      label: "Selecione a visão desejada:",
      value: visao,
      onChange: setVisao,
      options: visaoOptions,
      disabled: true,
    },
    {
      id: "mes",
      label: "Selecione o mês desejado:",
      value: mes,
      onChange: setMesSelecionado,
      options: mesOptions,
    },
    {
      id: "micro",
      label: "Selecione a Micro:",
      value: micro,
      onChange: setMicro,
      options: microOptions,
    },
  ];

  return (
    <>
      <NavTabs navs={tabs} centerTabs navBg="gray" />
      <div className="space-y-4 py-6">
      {isRefreshing && (
        <div className="container">
          <div className="w-[130px] flex mt-4 items-center gap-2 bg-[#01054C] rounded-md px-4 py-2 text-xs font-semibold text-[#fff] animate-pulse">
            <span className="rounded-full" />
            Atualizando...
          </div>
        </div>
      )}

        <VolumeSection selects={selects} cards={volumeCards} showClearButton={hasActiveFilters} onClearFilters={clearPageFilters} />
        <div className="hidden">
          <ReportFilterBar
            searchValue={searchValue}
            onSearchValueChange={setSearchValue}
            onSearchSubmit={setSearchValue}
            searchPlaceholder="Busque por Clientes, Código ou Grupo Centralizador"
            filterFields={[]}
            showClearButton={false}
            showOrder={false}
          />
        </div>

        <GraficoClienteStatic
          mes={defaultMes}
          faturamentos={faturamentosData}
          faturamentoLastCreatedAtDate={null}
          filtros={filtros}
          govs={govs}
          filters={filters}
          setFilters={setFilters}
          cleanFilters={cleanFilters}
        />
        <SelfServicesChart chartData={positivacoesChartData} title="Positivações" />
        <ClientShareChart
          chartData={hasGrupoOuProdutoFiltro ? clientShareChartData : null}
          title="Client Share - 4 meses"
          emptyMessage={
            hasGrupoOuProdutoFiltro
              ? "Sem dados de Client Share para os filtros selecionados"
              : "Filtre um Grupo de Produto ou Produto para visualizar as informações"
          }
        />

        <footer className="container py-5 text-center text-[#8D96A8]">
          <p className="text-sm">
            Dados atualizados em: <span className="font-bold text-[#091D4A]">{updatedAt}</span>
          </p>
          <p className="text-sm">Atualização uma vez ao dia com referência d-1</p>
        </footer>
      </div>
    </>
  );
};

export default CarteiraPage;
